import { createContext, FC, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
    INSS_MARGIN_PER_PAGE,
    InssMarginClientForm,
    InssMarginDiscardReason,
    InssMarginFieldErrors,
    InssMarginFinancial,
    InssMarginIndicators,
    InssMarginItem,
    InssMarginListResponse,
    InssMarginRecord,
    InssMarginTab,
    InssMarginTotals,
} from '../../models/InssMargin/InssMarginModels';
import { InssMarginService } from '../../services/InssMargin/InssMarginService';

// Tudo aqui vive só em memória: nenhum dado de cliente vai para localStorage,
// sessionStorage ou IndexedDB (regra da E1).

export interface InssMarginSaveResult {
    record?: InssMarginRecord;
    validatedAt?: string | null;
    fieldErrors?: InssMarginFieldErrors;
    error?: string;
}

interface InssMarginContextProps {
    tab: InssMarginTab;
    page: number;
    lastPage: number;
    search: string;
    items: InssMarginItem[];
    totals: InssMarginTotals;
    total: number;
    emptyMessage: string | null;
    financials: InssMarginFinancial[];
    discardReasons: InssMarginDiscardReason[];
    indicators: InssMarginIndicators | null;
    isInitialLoading: boolean;
    isListLoading: boolean;
    pendingFinancialIds: string[];
    changeTab: (tab: InssMarginTab) => void;
    goToPage: (page: number) => void;
    changeSearch: (search: string) => void;
    toggleFinancial: (item: InssMarginItem, financialId: string) => Promise<void>;
    discard: (item: InssMarginItem, reason: string, note?: string) => Promise<boolean>;
    restore: (item: InssMarginItem) => Promise<boolean>;
    getRecord: (cpf: string) => Promise<{ record?: InssMarginRecord; error?: string }>;
    validate: (item: InssMarginItem) => Promise<InssMarginSaveResult>;
    updateClient: (item: InssMarginItem, form: InssMarginClientForm) => Promise<InssMarginSaveResult>;
}

const InssMarginContext = createContext<InssMarginContextProps>({} as InssMarginContextProps);

const service = new InssMarginService();

const EMPTY_TOTALS: InssMarginTotals = { oportunidades: 0, descartadas: 0 };

// Espera o parceiro parar de digitar antes de buscar na carteira inteira.
const SEARCH_DEBOUNCE_MS = 400;

// Financeiras que aparecem na tela, na ordem de exibição, pelo nome do cadastro do Lacqua
// (GET /financeiras). As demais ficam de fora, mas as já marcadas no cliente são mantidas
// ao salvar (o toggle parte da lista completa do item).
const VISIBLE_FINANCIALS = [
    'bb mais',
    'banrisul',
    'bmg',
    'brb',
    'banco c6 consig',
    'banco caixa economica federal',
    'banco daycoval',
    'digio',
    'facta financeira',
    'finanto',
    'icred',
    'banco mercantil',
    'nbc bank',
    'paraná banco',
    'presenca bank',
    'quero + legado',
    'banco safra',
    'total cash',
    'vctex',
];

const onlyVisibleFinancials = (financials: InssMarginFinancial[]) => financials
    .map(financial => ({ financial, order: VISIBLE_FINANCIALS.indexOf(financial.name.trim().toLowerCase()) }))
    .filter(({ order }) => order >= 0)
    .sort((a, b) => a.order - b.order)
    .map(({ financial }) => financial);

export const InssMarginProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const [tab, setTab] = useState<InssMarginTab>('oportunidades');
    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [search, setSearch] = useState('');
    const [items, setItems] = useState<InssMarginItem[]>([]);
    const [totals, setTotals] = useState<InssMarginTotals>(EMPTY_TOTALS);
    const [total, setTotal] = useState(0);
    const [emptyMessage, setEmptyMessage] = useState<string | null>(null);
    const [financials, setFinancials] = useState<InssMarginFinancial[]>([]);
    const [discardReasons, setDiscardReasons] = useState<InssMarginDiscardReason[]>([]);
    // E1: só a carteira tem número (vem dos totais da lista); os demais cards ficam "Em breve".
    const indicators: InssMarginIndicators | null = { portfolio: totals.oportunidades, simulated: 0, typed: 0, typed_amount: 0 };
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [isListLoading, setIsListLoading] = useState(false);
    const [pendingFinancialIds, setPendingFinancialIds] = useState<string[]>([]);

    // Descarta respostas atrasadas quando o parceiro troca de aba/página/busca rápido.
    const requestSeq = useRef(0);
    const itemsRef = useRef<InssMarginItem[]>([]);
    itemsRef.current = items;
    const tabRef = useRef<InssMarginTab>('oportunidades');
    const searchRef = useRef('');
    const searchTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    const loadList = useCallback(async (nextTab: InssMarginTab, nextPage: number) => {
        const seq = ++requestSeq.current;
        tabRef.current = nextTab;
        setTab(nextTab);
        setPage(nextPage);
        setIsListLoading(true);

        const [response, error] = await service.list({
            tab: nextTab,
            page: nextPage,
            perPage: INSS_MARGIN_PER_PAGE,
            search: searchRef.current || undefined,
        });
        if (seq !== requestSeq.current) return;

        setIsListLoading(false);
        if (error) {
            toast.error(response?.message || error);
            return;
        }

        const data: InssMarginListResponse = response?.data;
        setItems(data?.items ?? []);
        setTotals(data?.totals ?? EMPTY_TOTALS);
        setTotal(data?.total ?? 0);
        setLastPage(data?.lastPage ?? 1);
        setEmptyMessage(data?.message ?? null);
    }, []);

    const loadAll = useCallback(async () => {
        const [[financialsResponse, financialsError], [reasonsResponse, reasonsError]] = await Promise.all([
            service.getFinancials(),
            service.getDiscardReasons(),
            loadList('oportunidades', 1),
        ]);

        if (financialsError) {
            toast.error(financialsResponse?.message || financialsError);
        } else {
            setFinancials(onlyVisibleFinancials(financialsResponse?.data ?? []));
        }
        if (reasonsError) {
            toast.error(reasonsResponse?.message || reasonsError);
        } else {
            setDiscardReasons(reasonsResponse?.data ?? []);
        }
    }, [loadList]);

    useEffect(() => {
        loadAll().then(() => setIsInitialLoading(false));
        return () => clearTimeout(searchTimer.current);
    }, [loadAll]);

    const changeTab = useCallback((nextTab: InssMarginTab) => {
        if (nextTab === tab) return;
        loadList(nextTab, 1);
    }, [tab, loadList]);

    const goToPage = useCallback((nextPage: number) => {
        loadList(tab, nextPage);
    }, [tab, loadList]);

    const changeSearch = useCallback((value: string) => {
        setSearch(value);
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => {
            const next = value.trim();
            if (next === searchRef.current) return;
            searchRef.current = next;
            loadList(tabRef.current, 1);
        }, SEARCH_DEBOUNCE_MS);
    }, [loadList]);

    const patchItem = useCallback((id: string, changes: Partial<InssMarginItem>) => {
        setItems(prev => prev.map(i => (i.id === id ? { ...i, ...changes } : i)));
    }, []);

    const toggleFinancial = useCallback(async (item: InssMarginItem, financialId: string) => {
        const current = itemsRef.current.find(i => i.id === item.id) ?? item;
        const next = current.financials.includes(financialId)
            ? current.financials.filter(f => f !== financialId)
            : [...current.financials, financialId];

        patchItem(item.id, { financials: next });
        setPendingFinancialIds(prev => [...prev, item.id]);

        const [response, error] = await service.setFinancials(item.id, next);

        setPendingFinancialIds(prev => prev.filter(id => id !== item.id));
        if (error) {
            patchItem(item.id, { financials: current.financials });
            toast.error(response?.message || error);
            return;
        }
        if (response?.data?.financials) patchItem(item.id, { financials: response.data.financials });
    }, [patchItem]);

    // Remove a linha da aba atual e passa uma unidade de uma aba para a outra nos
    // contadores (a API só devolve a nova situação). Se a página ficar vazia, volta uma
    // página (ou recarrega a primeira, para puxar os próximos itens ou o texto de lista vazia).
    const removeAfterMove = useCallback((item: InssMarginItem) => {
        const remaining = itemsRef.current.filter(i => i.id !== item.id);
        setItems(remaining);
        setTotals(prev => (tab === 'oportunidades'
            ? { oportunidades: prev.oportunidades - 1, descartadas: prev.descartadas + 1 }
            : { oportunidades: prev.oportunidades + 1, descartadas: prev.descartadas - 1 }));
        setTotal(prev => prev - 1);

        if (remaining.length === 0) {
            loadList(tab, page > 1 ? page - 1 : 1);
        }
    }, [tab, page, loadList]);

    const discard = useCallback(async (item: InssMarginItem, reason: string, note?: string) => {
        const [response, error] = await service.discard(item.id, reason, note);
        if (error) {
            toast.error(response?.message || error);
            return false;
        }
        removeAfterMove(item);
        return true;
    }, [removeAfterMove]);

    const restore = useCallback(async (item: InssMarginItem) => {
        const [response, error] = await service.restore(item.id);
        if (error) {
            toast.error(response?.message || error);
            return false;
        }
        removeAfterMove(item);
        return true;
    }, [removeAfterMove]);

    const getRecord = useCallback(async (cpf: string) => {
        const [response, error] = await service.getRecord(cpf);
        if (error) return { error: response?.message || error };
        return { record: response?.data as InssMarginRecord };
    }, []);

    const validate = useCallback(async (item: InssMarginItem): Promise<InssMarginSaveResult> => {
        const [response, error] = await service.validate(item.id);
        if (error) return { error: response?.message || error };
        const validatedAt = response?.data?.validated_at ?? null;
        patchItem(item.id, { validated_at: validatedAt });
        return { validatedAt };
    }, [patchItem]);

    // A API devolve a ficha já atualizada; a página atual é recarregada para a linha
    // refletir nome, celular e validação vindos da carteira.
    const updateClient = useCallback(async (item: InssMarginItem, form: InssMarginClientForm): Promise<InssMarginSaveResult> => {
        const [response, error] = await service.updateClient(item.cpf, form);
        if (error) {
            const fieldErrors: InssMarginFieldErrors | undefined = response?.data?.errors;
            if (fieldErrors && Object.keys(fieldErrors).length) return { fieldErrors };
            return { error: response?.message || error };
        }
        loadList(tab, page);
        return { record: response?.data as InssMarginRecord };
    }, [tab, page, loadList]);

    return (
        <InssMarginContext.Provider value={{
            tab,
            page,
            lastPage,
            search,
            items,
            totals,
            total,
            emptyMessage,
            financials,
            discardReasons,
            indicators,
            isInitialLoading,
            isListLoading,
            pendingFinancialIds,
            changeTab,
            goToPage,
            changeSearch,
            toggleFinancial,
            discard,
            restore,
            getRecord,
            validate,
            updateClient,
        }}>
            {children}
        </InssMarginContext.Provider>
    );
};

export const useInssMargin = () => useContext(InssMarginContext);
